import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import HitCard from 'Components/Card/HitCard/HitCard';
import PopupHitCard from 'Components/Card/HitCardPopup/PopupHitCard';
import useViewport from 'Hooks/useViewport';
import { IsChallenger } from 'Helpers/Internal/split-test';

/**
 * AdaptiveHitCard decides whether to render the interactive PopupHitCard
 * (hover popup with a screenshot / bundle-cover carousel) or the standard HitCard.
 *
 *
 * All props other than disablePopup are spread straight through to the chosen card component.
 * @param {Object} props
 * @param {Object} props.hit - The product hit; its type / display_type / cover_type drive the decision.
 * @param {boolean} props.disablePopup - Always render the standard HitCard. Set this for cards
 * inside a modal: the popup renders at the app root, beneath the modal and outside its focus
 * trap, so it can be neither seen properly nor used from there.
 */
const AdaptiveHitCard = ({ disablePopup, ...props }) => {
  const { isAboveMd } = useViewport();
  const { hit, coverTypeOverride } = props;

  const coverType = coverTypeOverride || hit?.cover_type;
  const isPortrait = coverType === 'portrait';
  const isMystery = hit?.mystery;
  const isGameOrDlc = ['game', 'dlc'].includes(hit?.type) || hit?.display_type === 'game';

  // 606PopupHitCardBundleCoverGrid: only bundle cards that could show the popup enter the test.
  // Mystery bundles are left out by !isMystery, as their bundle_covers would give away what's in them.
  // The same bundle_covers checks as HitCardQuickLook
  const isCoverGridBundle = ['bundle', 'pick-and-mix'].includes(hit?.type) &&
    Array.isArray(hit?.bundle_covers) && !isEmpty(hit.bundle_covers) &&
    !hit.bundle_covers.includes(null);
  const canInvokeTest = !disablePopup && isAboveMd && !isPortrait && !isMystery && !isGameOrDlc &&
    isCoverGridBundle;
  const bundleCoverGrid = canInvokeTest && IsChallenger('606PopupHitCardBundleCoverGrid');

  const usePopup = !disablePopup && isAboveMd && !isPortrait && !isMystery &&
    (isGameOrDlc || bundleCoverGrid);

  if (!usePopup) return <HitCard {...props} />;

  return <PopupHitCard {...props} bundleCoverGrid={bundleCoverGrid} />;
};

AdaptiveHitCard.propTypes = {
  hit: PropTypes.shape({
    cover_type: PropTypes.string,
    mystery: PropTypes.bool,
    type: PropTypes.string,
    display_type: PropTypes.string,
    bundle_covers: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  coverTypeOverride: PropTypes.string,
  disablePopup: PropTypes.bool,
};

AdaptiveHitCard.defaultProps = {
  hit: {},
  coverTypeOverride: null,
  disablePopup: false,
};

export default AdaptiveHitCard;
